import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  alpha,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  Add,
  ContentCopy,
  QrCode2,
  Share,
  Edit,
  Verified,
  OpenInNew,
  ExitToApp,
  Close,
  Delete,
} from '@mui/icons-material';
import { DialogActions, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { GradientCard, GlassCard, CoinCard, AnimatedButton } from '../../components/v2';
import { gradients } from '../../theme/v2Theme';
import { useAuthState } from '../../utils/useAuthState';
import { signOut } from '../../utils/auth';
import { useHistory } from 'react-router-dom';
import { getUserHandle, getUserAddresses } from '../../utils/handleManager';
import Coins from '../../assets/coins/coins.json';
import QRCode from 'react-qr-code';
import WAV from 'multicoin-address-validator';
import * as firebase from 'firebase/app';
import 'firebase/database';
import { hasEthAddress, handleToEnsStyle } from '../../utils/ensFormatter';

// styled components removed; styles will be applied inline via sx

const coinGradients: { [key: string]: string } = {
  BTC: gradients.sunset,
  ETH: gradients.ocean,
  USDC: gradients.success,
  USDT: gradients.success,
  MATIC: gradients.lavender,
  SOL: gradients.primary,
  BNB: gradients.sunset,
};

export const Dashboard: React.FC = () => {
  const { user: firebaseUser, loading: authLoading } = useAuthState();
  const history = useHistory();
  const [handle, setHandle] = useState<string>('');
  const [userAddresses, setUserAddresses] = useState<{ [key: string]: string }>({});
  const [loadingData, setLoadingData] = useState(true);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [bio, setBio] = useState<string>('Welcome to your crypto payment profile!');
  const [avatar, setAvatar] = useState<string | null>(null);
  
  // Stats state
  const [stats, setStats] = useState({
    profileViews: 0,
    paymentRequests: 0,
    shareClicks: 0,
  });
  
  // Edit Profile form state
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editBio, setEditBio] = useState('');
  const [editAvatar, setEditAvatar] = useState('');
  
  // Add Address form state
  const [selectedCoin, setSelectedCoin] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [addressError, setAddressError] = useState('');
  const [isAddressValid, setIsAddressValid] = useState(false);

  const coins = Coins as { [key: string]: any };

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !firebaseUser) {
      history.push('/');
    }
  }, [firebaseUser, authLoading, history]);

  // Fetch user handle and addresses
  useEffect(() => {
    async function loadUserData() {
      if (!firebaseUser?.uid) {
        setLoadingData(false);
        return;
      }

      try {
        // Get user's handle from Realtime DB
        const userHandle = await getUserHandle(firebaseUser.uid);

        if (userHandle) {
          setHandle(userHandle);
          // Get user's addresses
          const addresses = await getUserAddresses(userHandle);
          setUserAddresses(addresses);

          // Fetch bio and avatar from Firebase by uid
          try {
            const bioRef = firebase.database().ref(`/profiles/bio/${firebaseUser.uid}`);
            const bioSnapshot = await bioRef.once('value');
            if (bioSnapshot.exists()) {
              const bioValue = bioSnapshot.val();
              if (bioValue) setBio(bioValue);
            }

            const avatarRef = firebase.database().ref(`/profiles/avatar/${firebaseUser.uid}`);
            const avatarSnapshot = await avatarRef.once('value');
            if (avatarSnapshot.exists()) {
              const avatarValue = avatarSnapshot.val();
              if (avatarValue && avatarValue !== 'none' && avatarValue !== '') {
                setAvatar(avatarValue);
              }
            }
            
            // Fetch stats
            const statsRef = firebase.database().ref(`/stats/${firebaseUser.uid}`);
            const statsSnapshot = await statsRef.once('value');
            if (statsSnapshot.exists()) {
              const statsData = statsSnapshot.val();
              setStats({
                profileViews: statsData.profileViews || 0,
                paymentRequests: statsData.paymentRequests || 0,
                shareClicks: statsData.shareClicks || 0,
              });
            }
          } catch (fbError) {
            console.error('Error fetching bio/avatar from Firebase:', fbError);
            // Keep defaults if Firebase fetch fails
          }
        } else {
          // No handle registered yet - use email prefix as fallback
          setHandle(firebaseUser.email?.split('@')[0] || 'user');
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        setHandle(firebaseUser.email?.split('@')[0] || 'user');
      } finally {
        setLoadingData(false);
      }
    }

    if (firebaseUser && !authLoading) {
      loadUserData();
    }
  }, [firebaseUser, authLoading]);

  // Validate address when coin or address changes
  useEffect(() => {
    if (selectedCoin && newAddress) {
      const coinsData = Coins as { [key: string]: any };
      const coinInfo = coinsData[selectedCoin];
      
      try {
        let isValid = false;
        if (coinInfo.ERC20) {
          isValid = WAV.validate(newAddress, 'ETH');
        } else {
          isValid = WAV.validate(newAddress, selectedCoin);
        }
        setIsAddressValid(isValid);
      } catch (error) {
        console.error('Validation error:', error);
        setIsAddressValid(false);
      }
    } else {
      setIsAddressValid(false);
    }
  }, [selectedCoin, newAddress]);

  const user = {
    handle: handle,
    displayName: `@${handle}` || firebaseUser?.displayName || firebaseUser?.email?.split('@')[0] || 'User',
    bio: bio,
    email: firebaseUser?.email || '',
    emailVerified: firebaseUser?.emailVerified || false,
    avatar: avatar || firebaseUser?.photoURL || null,
  };

  // Convert addresses object to array
  const addressArray = Object.keys(userAddresses).map((coinKey) => {
    const coinData = coins[coinKey] || {};
    return {
      coin: coinData.name || coinKey,
      symbol: coinKey,
      address: userAddresses[coinKey],
      gradient: coinGradients[coinKey] || gradients.primary,
    };
  });

  // Count unique addresses (some coins may share the same address)
  const uniqueAddresses = new Set(Object.values(userAddresses)).size;

  const handleCopyLink = () => {
    const link = `https://addrs.to/${user.handle}`;
    navigator.clipboard.writeText(link);
    // Show toast notification
  };

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    // Show toast notification
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      history.push('/');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  const handleShowQRCode = () => {
    setShowQRCode(true);
  };

  const handleCloseQRCode = () => {
    setShowQRCode(false);
  };

  const handleShareProfile = () => {
    const link = `https://addrs.to/${user.handle}`;
    navigator.clipboard.writeText(link);
    setCopyFeedback('Profile link copied!');
    setTimeout(() => setCopyFeedback(null), 2000);
  };

  const handleEditProfile = () => {
    setEditDisplayName(user.displayName);
    setEditBio(user.bio);
    setEditAvatar(avatar || '');
    setShowEditProfile(true);
  };

  const handleCloseEditProfile = () => {
    setShowEditProfile(false);
    setEditDisplayName('');
    setEditBio('');
    setEditAvatar('');
  };

  const handleSaveProfile = async () => {
    if (!firebaseUser?.uid) return;
    
    try {
      // Update bio and avatar in Firebase by uid
      await firebase.database().ref(`/profiles/bio/${firebaseUser.uid}`).set(editBio);
      await firebase.database().ref(`/profiles/avatar/${firebaseUser.uid}`).set(editAvatar || null);
      
      // Update local state
      setBio(editBio);
      setAvatar(editAvatar || null);
      
      handleCloseEditProfile();
      setCopyFeedback('Profile updated successfully!');
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile');
    }
  };

  const handleOpenAddressModal = () => {
    setSelectedCoin('');
    setNewAddress('');
    setAddressError('');
    setShowAddressModal(true);
  };

  const handleCloseAddressModal = () => {
    setShowAddressModal(false);
    setSelectedCoin('');
    setNewAddress('');
    setAddressError('');
  };

    const handleAddAddress = async () => {
    if (!selectedCoin || !newAddress) {
      setAddressError('Please select a coin and enter an address');
      return;
    }

    if (!isAddressValid) {
      setAddressError('This address is not valid for the selected cryptocurrency');
      return;
    }

    try {
      // Save to Firebase using v1 pattern
      await firebase.database().ref(`coins/${selectedCoin}`).update({
        [handle]: newAddress,
      });
      
      // Update local state
      setUserAddresses(prev => ({
        ...prev,
        [selectedCoin]: newAddress,
      }));
      
      handleCloseAddressModal();
      setCopyFeedback('Address added successfully!');
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (error) {
      console.error('Error adding address:', error);
      setAddressError('Failed to add address. Please try again.');
    }
  };

    const handleRemoveAddress = async (coin: string) => {
    if (!window.confirm(`Remove ${coin} address?`)) return;
    
    try {
      // Delete from Firebase using v1 pattern
      await firebase.database().ref(`coins/${coin}/${handle}`).remove();
      
      // Update local state
      setUserAddresses(prev => {
        const updated = { ...prev };
        delete updated[coin];
        return updated;
      });
      
      setCopyFeedback(`${coin} address removed`);
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (error) {
      console.error('Error removing address:', error);
      alert('Failed to remove address');
    }
  };

  // Show loading spinner while checking auth or loading data
  if (authLoading || loadingData) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            {authLoading ? 'Checking authentication...' : 'Loading your profile...'}
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!firebaseUser) {
    return null;
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          background: gradients.mesh,
          py: 6,
          borderRadius: '0 0 40px 40px',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0) 100%)',
          },
        }}
      >
        <Container maxWidth="lg">
          <Box position="relative" zIndex={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 4 }}
            >
              <Typography variant="h4" fontWeight={700} sx={{ color: '#fff' }}>
                My Dashboard
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  sx={{
                    borderColor: '#fff',
                    color: '#fff',
                    '&:hover': {
                      borderColor: '#fff',
                      background: alpha('#fff', 0.1),
                    },
                  }}
                  startIcon={<OpenInNew />}
                  href={`/${user.handle}`}
                  target="_blank"
                >
                  View Public Profile
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    borderColor: '#fff',
                    color: '#fff',
                    '&:hover': {
                      borderColor: '#fff',
                      background: alpha('#fff', 0.1),
                    },
                  }}
                  startIcon={<ExitToApp />}
                  onClick={handleSignOut}
                >
                  Sign Out
                </Button>
              </Stack>
            </Stack>

            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md="auto">
                <Avatar
                  src={user.avatar || undefined}
                  sx={{
                    width: 120,
                    height: 120,
                    border: '4px solid white',
                    boxShadow: '0px 8px 32px rgba(0,0,0,0.15)',
                    fontSize: '3rem',
                    fontWeight: 800,
                    background: gradients.secondary,
                  }}
                >
                  {user.displayName.charAt(0)}
                </Avatar>
              </Grid>
              <Grid item xs={12} md>
                <Stack spacing={2}>
                  <Box>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                      <Typography variant="h4" fontWeight={800} sx={{ color: '#fff' }}>
                        {user.displayName}
                      </Typography>
                      {user.emailVerified && (
                        <Tooltip title="Email Verified">
                          <Verified sx={{ color: '#10b981' }} />
                        </Tooltip>
                      )}
                    </Stack>
                    <Typography variant="body1" sx={{ color: alpha('#fff', 0.9), mt: 1 }}>
                      {user.bio}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip
                      label={`@${user.handle}`}
                      color="primary"
                      icon={<ContentCopy />}
                      onClick={handleCopyLink}
                      sx={{
                        background: alpha('#ffffff', 0.2),
                        backdropFilter: 'blur(10px)',
                        color: '#fff',
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        py: 3,
                        px: 2,
                        height: 'auto',
                        borderRadius: 2,
                        boxShadow: '0px 4px 16px rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: alpha('#ffffff', 1),
                          boxShadow: '0px 8px 24px rgba(0,0,0,0.2)',
                        },
                        '& .MuiChip-label': {
                          px: 2,
                        },
                      }}
                    />
                    {/* Show ENS if user has ETH address */}
                    {hasEthAddress(userAddresses) && (
                      <Chip
                        label={handleToEnsStyle(user.handle)}
                        color="primary"
                        icon={<ContentCopy />}
                        onClick={() => {
                          navigator.clipboard.writeText(handleToEnsStyle(user.handle));
                          setCopyFeedback('ENS copied!');
                          setTimeout(() => setCopyFeedback(null), 2000);
                        }}
                        sx={{
                          background: alpha('#ffffff', 0.2),
                          backdropFilter: 'blur(10px)',
                          color: '#fff',
                          fontSize: '1rem',
                          fontWeight: 700,
                          py: 3,
                          px: 2,
                          height: 'auto',
                          borderRadius: 2,
                          boxShadow: '0px 4px 16px rgba(0,0,0,0.1)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: alpha('#ffffff', 1),
                            boxShadow: '0px 8px 24px rgba(0,0,0,0.2)',
                          },
                          '& .MuiChip-label': {
                            px: 2,
                          },
                        }}
                      />
                    )}
                    <Tooltip title="Show QR Code">
                      <IconButton
                        sx={{
                          background: alpha('#fff', 0.2),
                          color: '#fff',
                          '&:hover': { background: alpha('#fff', 0.3) },
                        }}
                        onClick={handleShowQRCode}
                      >
                        <QrCode2 />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Share Profile">
                      <IconButton
                        sx={{
                          background: alpha('#fff', 0.2),
                          color: '#fff',
                          '&:hover': { background: alpha('#fff', 0.3) },
                        }}
                        onClick={handleShareProfile}
                      >
                        <Share />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Profile">
                      <IconButton
                        sx={{
                          background: alpha('#fff', 0.2),
                          color: '#fff',
                          '&:hover': { background: alpha('#fff', 0.3) },
                        }}
                        onClick={handleEditProfile}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mt: -4, pb: 8 }}>
        {/* Quick Stats */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={6} md={3}>
            <GlassCard sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h3" fontWeight={800} color="primary.main">
                {uniqueAddresses}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Active Addresses
              </Typography>
            </GlassCard>
          </Grid>
          <Grid item xs={6} md={3}>
            <GlassCard sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h3" fontWeight={800} color="secondary.main">
                {Object.keys(userAddresses).length}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Tokens Accepted
              </Typography>
            </GlassCard>
          </Grid>
          <Grid item xs={6} md={3}>
            <GlassCard sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h3" fontWeight={800} color="success.main">
                {stats.profileViews}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Profile Views
              </Typography>
            </GlassCard>
          </Grid>
          <Grid item xs={6} md={3}>
            <GlassCard sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h3" fontWeight={800} color="warning.main">
                {stats.paymentRequests}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Payment Requests
              </Typography>
            </GlassCard>
          </Grid>
        </Grid>

        {/* Addresses Section */}
        <Box sx={{ mb: 4 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Your Crypto Addresses
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage all your cryptocurrency addresses in one place
              </Typography>
            </Box>
            <AnimatedButton
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenAddressModal}
              sx={{
                background: gradients.primary,
                '&:hover': {
                  background: gradients.primary,
                  opacity: 0.9,
                },
              }}
            >
              Add Address
            </AnimatedButton>
          </Stack>

          <Grid container spacing={3}>
            {addressArray.map((address, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <CoinCard
                  coinName={address.coin}
                  coinSymbol={address.symbol}
                  address={address.address}
                  gradient={address.gradient}
                  handle={user.handle}
                  userAddresses={userAddresses}
                  onCopy={() => {
                    // Note: amount will be handled internally in CoinCard
                    const link = `https://addrs.to/${user.handle}/${address.symbol.toLowerCase()}`;
                    navigator.clipboard.writeText(link);
                    setCopyFeedback('Link copied!');
                    setTimeout(() => setCopyFeedback(null), 2000);
                  }}
                  onSend={() => {}}
                  onDelete={() => handleRemoveAddress(address.symbol)}
                />
              </Grid>
            ))}

            {/* Add New Card */}
            <Grid item xs={12} sm={6} lg={4}>
              <GlassCard
                onClick={handleOpenAddressModal}
                sx={{
                  height: '100%',
                  minHeight: 240,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  border: '2px dashed',
                  borderColor: alpha('#6366f1', 0.3),
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#6366f1',
                    background: alpha('#6366f1', 0.05),
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Add sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  Add New Address
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Support for 32+ cryptocurrencies
                </Typography>
              </GlassCard>
            </Grid>
          </Grid>
        </Box>

        {/* Quick Actions */}
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
            Quick Actions
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <GradientCard
                onClick={handleShareProfile}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: gradients.ocean,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(8, 145, 178, 0.3)',
                  },
                }}
              >
                <Share sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6" fontWeight={700}>
                  Share Profile
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {copyFeedback || 'Copy your profile link'}
                </Typography>
              </GradientCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <GradientCard
                onClick={handleEditProfile}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: gradients.success,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(16, 185, 129, 0.3)',
                  },
                }}
              >
                <Edit sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6" fontWeight={700}>
                  Edit Profile
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Update your info
                </Typography>
              </GradientCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <GradientCard
                onClick={handleOpenAddressModal}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: gradients.lavender,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(168, 85, 247, 0.3)',
                  },
                }}
              >
                <Add sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6" fontWeight={700}>
                  Add Address
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Add new crypto address
                </Typography>
              </GradientCard>
            </Grid>
          </Grid>

          {/* QR Code Dialog */}
          <Dialog
            open={showQRCode}
            onClose={handleCloseQRCode}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '24px',
                backdropFilter: 'blur(10px)',
              }
            }}
          >
            <DialogTitle sx={{ 
              pb: 1,
              fontSize: '1.5rem',
              fontWeight: 700,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              Your Profile QR Code
              <IconButton
                onClick={handleCloseQRCode}
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 4 }}>
              <Box sx={{
                p: 3,
                background: alpha('#ffffff', 0.8),
                borderRadius: '16px',
                border: `1px solid ${alpha('#000', 0.1)}`,
                backdropFilter: 'blur(10px)',
              }}>
                <QRCode 
                  value={`https://addrs.to/${user.handle}`}
                  size={256}
                  level="H"
                />
              </Box>
              <Stack direction="column" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Share this QR code for quick access
                </Typography>
                <Typography 
                  variant="body2" 
                  color="primary.main" 
                  fontWeight={600}
                  sx={{ 
                    wordBreak: 'break-all',
                    p: 2,
                    background: alpha('#6366f1', 0.1),
                    borderRadius: '10px',
                    cursor: 'pointer',
                    '&:hover': {
                      background: alpha('#6366f1', 0.2),
                    },
                    transition: 'background 0.2s',
                  }}
                  onClick={handleCopyLink}
                >
                  https://addrs.to/{user.handle}
                </Typography>
              </Stack>
            </DialogContent>
          </Dialog>

          {/* Edit Profile Dialog */}
          <Dialog
            open={showEditProfile}
            onClose={handleCloseEditProfile}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '24px',
                backdropFilter: 'blur(10px)',
              }
            }}
          >
            <DialogTitle sx={{ 
              pb: 1,
              fontSize: '1.5rem',
              fontWeight: 700,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              Edit Profile
              <IconButton
                onClick={handleCloseEditProfile}
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Update your display name, bio, and avatar to customize your profile.
              </Typography>
              
              <TextField
                label="Display Name"
                fullWidth
                value={editDisplayName}
                onChange={(e) => setEditDisplayName(e.target.value)}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  }
                }}
              />
              <TextField
                label="Bio"
                fullWidth
                multiline
                rows={3}
                value={editBio}
                onChange={(e) => setEditBio(e.target.value)}
                placeholder="Tell us about yourself..."
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  }
                }}
              />
              <TextField
                label="Avatar URL"
                fullWidth
                value={editAvatar}
                onChange={(e) => setEditAvatar(e.target.value)}
                placeholder="Enter avatar image URL"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  }
                }}
              />
              {editAvatar && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Avatar Preview
                  </Typography>
                  <Avatar
                    src={editAvatar}
                    sx={{
                      width: 80,
                      height: 80,
                      margin: '0 auto',
                    }}
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3, gap: 1 }}>
              <Button 
                onClick={handleCloseEditProfile}
                sx={{ 
                  borderRadius: '10px',
                  px: 3,
                  textTransform: 'none',
                  fontSize: '1rem',
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveProfile}
                variant="contained"
                sx={{ 
                  borderRadius: '10px',
                  px: 4,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: gradients.primary,
                  '&:hover': {
                    background: gradients.primary,
                    opacity: 0.9,
                  }
                }}
              >
                Save Changes
              </Button>
            </DialogActions>
          </Dialog>

          {/* Add Address Dialog */}
          <Dialog
            open={showAddressModal}
            onClose={handleCloseAddressModal}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '24px',
                backdropFilter: 'blur(10px)',
              }
            }}
          >
            <DialogTitle sx={{ 
              pb: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '1.5rem',
              fontWeight: 700,
            }}>
              Add New Cryptocurrency Address
              <IconButton
                onClick={handleCloseAddressModal}
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Select a cryptocurrency and enter a valid wallet address to add it to your profile.
              </Typography>

              {addressError && (
                <Box sx={{
                  p: 2,
                  borderRadius: '12px',
                  background: alpha('#ef4444', 0.1),
                  border: `1px solid ${alpha('#ef4444', 0.3)}`,
                }}>
                  <Typography color="error" variant="body2" fontWeight={600}>
                    {addressError}
                  </Typography>
                </Box>
              )}

              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '0.95rem' }}>Cryptocurrency</InputLabel>
                <Select
                  value={selectedCoin}
                  label="Cryptocurrency"
                  onChange={(e) => {
                    setSelectedCoin(e.target.value);
                    setAddressError('');
                  }}
                  sx={{
                    borderRadius: '12px',
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.95rem',
                    }
                  }}
                >
                  {Object.keys(coins).map((coin) => (
                    <MenuItem key={coin} value={coin}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                        <Typography fontWeight={600}>{coin}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {coins[coin].name}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Wallet Address"
                fullWidth
                multiline
                rows={2}
                value={newAddress}
                onChange={(e) => {
                  setNewAddress(e.target.value);
                  setAddressError('');
                }}
                placeholder="Enter your wallet address"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    fontSize: '0.9rem',
                  }
                }}
              />

              {newAddress && selectedCoin && (
                <Box sx={{
                  p: 2,
                  borderRadius: '12px',
                  background: alpha(isAddressValid ? '#10b981' : '#ef4444', 0.1),
                  border: `2px solid ${isAddressValid ? '#10b981' : '#ef4444'}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}>
                  <Box 
                    sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%',
                      backgroundColor: isAddressValid ? '#10b981' : '#ef4444',
                      flexShrink: 0,
                    }} 
                  />
                  <Typography 
                    variant="body2" 
                    fontWeight={600}
                    color={isAddressValid ? 'success.main' : 'error.main'}
                  >
                    {isAddressValid 
                      ? '✓ Address is valid for ' + selectedCoin
                      : '✗ Address is not valid for ' + selectedCoin
                    }
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3, gap: 1 }}>
              <Button 
                onClick={handleCloseAddressModal}
                sx={{ 
                  borderRadius: '10px',
                  px: 3,
                  textTransform: 'none',
                  fontSize: '1rem',
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddAddress}
                variant="contained"
                disabled={!selectedCoin || !newAddress || !isAddressValid}
                sx={{ 
                  borderRadius: '10px',
                  px: 4,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: gradients.primary,
                  '&:hover': {
                    background: gradients.primary,
                    opacity: 0.9,
                  },
                  '&:disabled': {
                    background: alpha('#cccccc', 0.5),
                    color: alpha('#000000', 0.5),
                  }
                }}
              >
                Add Address
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </Box>
  );
};
